import { useUnit } from 'effector-react'
import { useMutation } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { toast } from '@/models/toasts.ts'
import { creatorSettingsService } from '@/modules/creator-settings/service/creator-settings-service.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider'
import type {
  SettingsFanMaxNumber,
  SettingsReactivator,
} from '@/modules/creator-settings/types'
import { handleError } from '@/utils/error'

// Todo: refactor please
export const useAutomationsMutations = () => {
  const { setSettingsData } = useSettingsContext()
  const creatorId = useUnit($creatorId)

  const promotionEnabled = useMutation(
    (data: SettingsReactivator) =>
      creatorSettingsService.togglePromotionReactivator(creatorId!, data),
    {
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while enabling promotion reactivator',
        )
      },
    },
  )

  const promotionDays = useMutation(
    (days: number) => {
      if (!days) {
        throw new Error('The period cannot be 0 days')
      }

      return creatorSettingsService.setReactivatorDays(creatorId!, { days })
    },
    {
      onSuccess: (_, days) => {
        setSettingsData((prevState) => ({
          ...prevState,
          defaultPromotionFinishDays: days,
        }))
        toast.success('Successfully set promotion days')
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while updating promotion days')
      },
    },
  )

  const enableFanNumbering = useMutation(
    (data: SettingsReactivator) =>
      creatorSettingsService.toggleFanNumbering(creatorId!, data),
    {
      onError: (error) => {
        handleError(error, 'Something went wrong while enabling fan numbering')
      },
    },
  )

  const fanNumberingNumbers = useMutation(
    (data: SettingsFanMaxNumber) =>
      creatorSettingsService.setFanNumberingMaxNumber(creatorId!, data),
    {
      onSuccess: () => {
        toast.success(
          'Successfully set max fan numbers. This change may take up to 60 seconds to take effect in the extension.',
        )
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while setting max fan numbers')
      },
    },
  )

  const enableAutoFollow = useMutation(
    (data: SettingsReactivator) =>
      creatorSettingsService.toggleAutoFollowExpiredFans(creatorId!, data),
    {
      onError: (error) => {
        handleError(error, 'Something went wrong while enabling auto follow')
      },
    },
  )

  const autoFollowMinimumSpend = useMutation(
    (amount: number) =>
      creatorSettingsService.setAutoFollowMinimumSpending(creatorId!, amount),
    {
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while setting auto follow minimum spending',
        )
      },
    },
  )

  const enableAutoUnFollow = useMutation(
    (data: SettingsReactivator) =>
      creatorSettingsService.toggleAutoUnFollowExpiredFans(creatorId!, data),
    {
      onError: (error) => {
        handleError(error, 'Something went wrong while enabling auto unfollow')
      },
    },
  )

  const autoUnFollowMinimumSpend = useMutation(
    (amount: number) =>
      creatorSettingsService.setAutoUnFollowMinimumSpending(creatorId!, amount),
    {
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while setting auto unfollow minimum spending',
        )
      },
    },
  )

  const autoUnFollowDays = useMutation(
    (amount: number) =>
      creatorSettingsService.setAutoUnFollowDays(creatorId!, amount),
    {
      onSuccess: (_, days) => {
        setSettingsData((prevState) => ({
          ...prevState,
          autoUnfollowAfterFollowingDays: days,
        }))

        toast.success('Successfully set the minimum days to un-follow fans')
      },
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while updating minimum days to un-follow fans',
        )
      },
    },
  )

  return {
    enablePromotion: promotionEnabled,
    setPromotionDays: promotionDays.mutate,
    enableFanNumbering: enableFanNumbering,
    setFanNumberingMaxNums: fanNumberingNumbers,
    enableAutoFollow: enableAutoFollow,
    setAutoFollowMinimumSpending: autoFollowMinimumSpend,
    enableAutoUnFollow: enableAutoUnFollow,
    setAutoUnFollowMinimumSpend: autoUnFollowMinimumSpend,
    setAutoUnFollowDays: autoUnFollowDays.mutate,
  }
}
